import { useCallback, useEffect, useMemo, useRef } from "react";
import { CurrencyType, PaymentTypes } from "../../../../common/enums";
import { PaymentTypesLabels } from "../../../../common/labels";
import { formatSignedEurValue } from "../../../../common/utilities/currencyUtility";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";

/**
 * The "Депозит" entry offered in the "Начин на плащане" dropdown. It is NEVER
 * part of paymentUtility's option lists (studio settings, cashdesk, gym, ...)
 * — it exists only on the two class-reservation surfaces (arrival
 * confirmation + add payment method), appended via useDepositTenderOption.
 */
export const depositPaymentOption: ISelectFieldModel = {
  id: PaymentTypes.Deposit,
  name: PaymentTypesLabels[PaymentTypes.Deposit],
};

interface IUseDepositTenderOptionProps {
  /** The client's current EUR deposit balance (0/undefined when none). */
  depositBalance?: number | null;
  /**
   * The full class/service price being charged (the form's price field). This
   * is the RAW per-keystroke value, so it can momentarily be "" or garbage.
   */
  price?: number | string | null;
  /**
   * The currency selected in the price field's € ▼ dropdown (the form's
   * currencyId field, a CurrencyType). Deposit payment is EUR-only.
   */
  selectedCurrencyId?: number | null;
  /** The studio-configured payment options (Deposit is never among them). */
  baseOptions: ISelectFieldModel[];
  /** The currently selected payment type in the form. */
  selectedPaymentTypeId?: number | null;
  /**
   * True when the reservation is ALREADY stamped PaymentType = Deposit (paid
   * from the deposit earlier). The Spend row has already left the ledger, so
   * the balance no longer covers the price — re-validating the existing tender
   * against the balance would silently re-tender the class and double-charge
   * the client, so "Депозит" stays offered and selected.
   */
  isDepositAlreadyTendered?: boolean;
  /**
   * Called when Deposit is the current selection but stopped being available
   * (price raised above the balance, currency switched to BGN, ...). Receives
   * the id of the first configured option to fall back to (0 when none).
   */
  onDepositUnavailable: (fallbackPaymentTypeId: number) => void;
  /**
   * Called when a Deposit selection that this hook had to replace becomes
   * valid again (the staff corrected an overshoot price) AND the staff have
   * not picked a method themselves in the meantime. Receives
   * PaymentTypes.Deposit so the consumer can re-select it.
   */
  onDepositRestored?: (depositPaymentTypeId: number) => void;
}

/**
 * Tender-model deposit payment (contract-final):
 *   • "Депозит" is a regular option in the "Начин на плащане" dropdown on the
 *     arrival-confirmation page and the add-payment dialog ONLY;
 *   • it is offered only under the FULL-COVERAGE rule — deposit balance >=
 *     the current price — and only for EUR prices (deposits are EUR-only);
 *   • a reservation already tendered with the deposit keeps the option
 *     regardless of the (already debited) balance;
 *   • it is appended AFTER the studio-configured options, so it is never
 *     preselected by default and picking another method stays possible;
 *   • submitting simply sends paymentTypeId 900 — no split fields, no
 *     useDeposit flag; the BE re-validates everything (error 31006/31008).
 */
export const useDepositTenderOption = ({
  depositBalance,
  price,
  selectedCurrencyId,
  baseOptions,
  selectedPaymentTypeId,
  isDepositAlreadyTendered = false,
  onDepositUnavailable,
  onDepositRestored,
}: IUseDepositTenderOptionProps) => {
  const isDepositSelected = selectedPaymentTypeId === PaymentTypes.Deposit;

  const result = useMemo(() => {
    const balance = !!depositBalance && depositBalance > 0 ? depositBalance : 0;

    // The price field pushes EVERY keystroke through onChange (no blur gate,
    // no debounce), so "" / "-" / "1e" are TRANSIENT states while staff edit
    // the number — never a reason to drop an already-picked tender.
    const rawPrice =
      price === null || price === undefined ? "" : String(price).trim();
    const parsedPrice = rawPrice === "" ? Number.NaN : Number(rawPrice);
    const isPriceTransient = !Number.isFinite(parsedPrice);
    const fullPrice = isPriceTransient || parsedPrice <= 0 ? 0 : parsedPrice;

    const hasDepositBalance = balance > 0;

    // Deposits are EUR-only: an explicit non-EUR selection blocks the option;
    // null/undefined means the caller has no currency selector, which stays
    // deposit-compatible (prices without a selector are EUR by convention).
    const isNonEurCurrency =
      selectedCurrencyId !== null &&
      selectedCurrencyId !== undefined &&
      selectedCurrencyId !== CurrencyType.EUR;

    // Full-coverage rule: the deposit must cover the WHOLE price. Partial
    // coverage is not a thing in the tender model — staff top up the
    // difference in the client's profile first.
    const coversFullPrice =
      hasDepositBalance && fullPrice > 0 && balance >= fullPrice;

    const isDepositSelectable =
      !isNonEurCurrency && (isDepositAlreadyTendered || coversFullPrice);

    // While the price is half-typed the option stays in the list so the
    // dropdown keeps showing the current "Депозит" selection.
    const showDepositOption =
      isDepositSelectable || (isDepositSelected && isPriceTransient);

    return {
      hasDepositBalance,
      isDepositSelectable,
      /** True while the price input holds an empty/unparseable value. */
      isPriceTransient,
      paymentOptions: showDepositOption
        ? [...baseOptions, depositPaymentOption]
        : baseOptions,
      /** e.g. "+€ 10.00" — the green balance shown in the details box. */
      depositBalanceLabel: formatSignedEurValue(balance),
    };
  }, [
    depositBalance,
    price,
    selectedCurrencyId,
    baseOptions,
    isDepositAlreadyTendered,
    isDepositSelected,
  ]);

  // A selection that stopped being available never reaches the payload: fall
  // back to the first configured method (the same one preselection uses). The
  // replaced choice is remembered so a corrected price can restore it — the
  // fallback must not LATCH the tender off after a transient overshoot.
  const { isDepositSelectable, isPriceTransient } = result;
  const forcedFallbackRef = useRef<number | null>(null);

  useEffect(() => {
    if (isDepositSelected) {
      if (isDepositSelectable || isPriceTransient) {
        return;
      }

      const fallbackPaymentTypeId = Number(baseOptions[0]?.id ?? 0);
      forcedFallbackRef.current = fallbackPaymentTypeId;
      onDepositUnavailable(fallbackPaymentTypeId);
      return;
    }

    // Депозит became available again while the form still holds the fallback
    // THIS hook forced and NOBODY touched the dropdown since — restore the
    // staff's original choice. Any other selection, or any deliberate pick
    // (see notifyPaymentTypeSelectedByUser), means staff chose the method
    // themselves, so it is left alone.
    if (
      isDepositSelectable &&
      forcedFallbackRef.current !== null &&
      selectedPaymentTypeId === forcedFallbackRef.current
    ) {
      forcedFallbackRef.current = null;
      onDepositRestored?.(PaymentTypes.Deposit);
    }
  }, [
    selectedPaymentTypeId,
    isDepositSelected,
    isDepositSelectable,
    isPriceTransient,
    baseOptions,
    onDepositUnavailable,
    onDepositRestored,
  ]);

  /**
   * Wire this to the "Начин на плащане" dropdown: it must be called whenever
   * the STAFF pick an option themselves — including re-picking the option the
   * form already holds, which is exactly the method this hook forced after an
   * overshoot price. A deliberate pick cancels the pending restore, so
   * lowering the price afterwards can no longer flip the tender back to
   * Депозит behind the staff's back (they would take the money in cash while
   * the client's deposit gets debited). Price edits alone never call it, so a
   * purely transient overshoot still restores Депозит.
   */
  const notifyPaymentTypeSelectedByUser = useCallback(() => {
    forcedFallbackRef.current = null;
  }, []);

  return { ...result, notifyPaymentTypeSelectedByUser };
};
